import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';

// CONSTANTS
import { LOCATIONS_FREE_TV_LANDING_PAGE } from '@/constants/locations';

// UTILITY
import { customAnalyticsEvent, externalLink, getExternalUrlParams } from '@/utility/analytics';
import { updateUrl } from '@/utility/common';
import { checkIsAbsoluteUrl } from '@/utility/routes';

// COMPONENTS
import CollapsableContainer from '@/components/CollapsableContainer';
import RouterLink from '@/components/Link';
import ReactHtml from '@/components/ReactHtml';

// STYLES
import { Container } from './styles';

function CustomLink({ href, ...restProps }) {
  if (!href) return <span />;

  const externalUrlParams = getExternalUrlParams();
  const link = updateUrl(href, externalUrlParams);
  const isExternalLink = checkIsAbsoluteUrl(href);

  function handleExternalLinkClicked() {
    externalLink({ href: link });
  }

  let linkProps = { to: link, component: RouterLink };

  if (isExternalLink) {
    linkProps = {
      href: link,
      onClick: () => handleExternalLinkClicked(link),
    };
  }

  return (
    <Link
      className="text-bold"
      target="_blank"
      color="inherit"
      underline="always"
      {...linkProps}
      {...restProps}
    />
  );
}

CustomLink.propTypes = {
  href: PropTypes.string,
};

const textComponents = {
  b: props => <b className="text-medium" {...props} />,
  p: props => <Typography className="p3 text-regular" variant="subtitle2" {...props} />,
  ol: props => <ol className="m-0 p-l-30" {...props} />,
  ul: props => <ul className="m-0 p-l-30" {...props} />,
  li: props => (
    <Typography className="m-b-10 body3" variant="subtitle2" component="li" {...props} />
  ),
  span: props => <span className="text-color--primary text-medium" {...props} />,
  a: props => <CustomLink {...props} />,
};

const FAQ = ({
  data = [],
  className,
  withBorder,
  containerProps,
  title,
  viewMoreLink,
  viewMoreText,
}) => {
  const [t] = useTranslation();
  const [faqCollapses, setFaqCollapses] = useState(
    Array.from({ length: data.length }, () => false),
  );

  const handleFaqCollapse = index => {
    const faqCollapsesCopy = faqCollapses.map((item, idx) => (idx === index ? !item : false));

    setFaqCollapses(faqCollapsesCopy);
  };

  const handleCollapseEntered = (question, slot) => {
    customAnalyticsEvent('FAQ opens', { question, slot, location: LOCATIONS_FREE_TV_LANDING_PAGE });
  };

  return (
    <Container
      className={clsx('faq section', className, withBorder && 'faq--with-border')}
      {...containerProps}
    >
      <div className="page-content">
        <Typography className="faq__title" align="center" component="h2" variant="h3">
          <ReactHtml component="span">{title || t('common.faqs')}</ReactHtml>
        </Typography>
        <div className="faq__list">
          {data.map((item, index) => (
            <CollapsableContainer
              key={index}
              title={item.title}
              open={faqCollapses[index]}
              handleCollapse={() => handleFaqCollapse(index)}
              collapseProps={{
                onEntered: () => handleCollapseEntered(item.title, index + 1),
              }}
            >
              <ReactHtml className="p3 text-regular markdown" components={textComponents}>
                {item.text}
              </ReactHtml>
            </CollapsableContainer>
          ))}
        </div>
        {!!viewMoreLink && (
          <div className="text-center">
            <Button className="m-t-40" href={viewMoreLink} target="_blank">
              {viewMoreText || t('buttons.viewMore')}
            </Button>
          </div>
        )}
      </div>
    </Container>
  );
};

FAQ.defaultProps = {
  className: '',
  title: '',
  withBorder: false,
  viewMoreLink: '',
  viewMoreText: '',
  containerProps: {},
};

FAQ.propTypes = {
  data: PropTypes.array.isRequired,
  className: PropTypes.string,
  withBorder: PropTypes.bool,
  containerProps: PropTypes.object,
  title: PropTypes.string,
  viewMoreLink: PropTypes.string,
  viewMoreText: PropTypes.string,
};

export default React.memo(FAQ);
